import { relativeLuminance } from './concierge-luminance';

// Order defines this module's canonical (alphabetized) key ordering.
export const STRAIN_TYPE_COLOR_KEYS = [
  'cbd',
  'cbg',
  'highCBD',
  'hybrid',
  'indica',
  'indicaHybrid',
  'sativa',
  'sativaHybrid',
  'seed',
  'thc',
] as const;

export type StrainTypeColorKey = typeof STRAIN_TYPE_COLOR_KEYS[number];

export type StrainTypeColors = Partial<Record<StrainTypeColorKey, string | null | undefined>>;

/** Default pill background used whenever a strain type has no configured color, or doesn't match any known key. */
export const DEFAULT_STRAIN_TYPE_PILL_BACKGROUND = '#e3e7e9';

/** Matches the existing hardcoded pill text color in card-strain-type.tsx. */
export const STRAIN_TYPE_PILL_DARK_TEXT = '#2c3236';
export const STRAIN_TYPE_PILL_LIGHT_TEXT = '#ffffff';

const STRAIN_TYPE_DISPLAY_LABELS: Record<StrainTypeColorKey, string> = {
  cbd: 'CBD',
  cbg: 'CBG',
  highCBD: 'High CBD',
  hybrid: 'Hybrid',
  indica: 'Indica',
  indicaHybrid: 'Indica-Hybrid',
  sativa: 'Sativa',
  sativaHybrid: 'Sativa-Hybrid',
  seed: 'Seed',
  thc: 'THC',
};

function normalizeStrainType(value: string): string {
  return value.toLowerCase().replace(/[\s-]/g, '');
}

const NORMALIZED_LABEL_TO_KEY = STRAIN_TYPE_COLOR_KEYS.reduce<Record<string, StrainTypeColorKey>>((acc, key) => {
  acc[normalizeStrainType(STRAIN_TYPE_DISPLAY_LABELS[key])] = key;
  return acc;
}, {});

/** Resolves a free-text strain type (e.g. "High CBD", "indica-hybrid") to its StrainTypeColors key, if any. */
export function getStrainTypeColorKey(strainType: string | null | undefined): StrainTypeColorKey | null {
  if (!strainType) {
    return null;
  }
  return NORMALIZED_LABEL_TO_KEY[normalizeStrainType(strainType)] ?? null;
}

/** Resolves the pill background for a given strain type + configured colors, falling back to the default. */
export function getStrainTypePillBackground(
  strainType: string | null | undefined,
  colors: StrainTypeColors | null | undefined,
  fallbackColor?: string | null
): string {
  const key = getStrainTypeColorKey(strainType);
  const color = key ? colors?.[key] : null;
  // Intentional `||`, not `??` — an empty-string color (unset via the color picker) should
  // also fall back to the default, not render as `background: ;`.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return color || fallbackColor || DEFAULT_STRAIN_TYPE_PILL_BACKGROUND;
}

// WCAG relative luminance below this is treated as a "dark" background needing light text.
const DARK_BACKGROUND_LUMINANCE_THRESHOLD = 0.5;

/** Picks readable pill text color for a resolved (already-defaulted) hex background. */
export function getStrainTypePillTextColor(background: string): string {
  return relativeLuminance(background) < DARK_BACKGROUND_LUMINANCE_THRESHOLD
    ? STRAIN_TYPE_PILL_LIGHT_TEXT
    : STRAIN_TYPE_PILL_DARK_TEXT;
}

/** Maps a StrainTypeColorKey to its kebab-case CSS custom property suffix. */
export const STRAIN_TYPE_CSS_SUFFIX: Record<StrainTypeColorKey, string> = {
  cbd: 'cbd',
  cbg: 'cbg',
  highCBD: 'high-cbd',
  hybrid: 'hybrid',
  indica: 'indica',
  indicaHybrid: 'indica-hybrid',
  sativa: 'sativa',
  sativaHybrid: 'sativa-hybrid',
  seed: 'seed',
  thc: 'thc',
};
